import React, { useEffect, useState } from "react";
import { 
  FiSearch,
  FiAlertCircle,
  FiClock,
  FiBarChart2,
  FiCalendar,
  FiTrendingUp,
  FiActivity
} from "react-icons/fi";
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line,
  CartesianGrid
} from 'recharts';
import "./home.css";
import { useSelector } from "react-redux";
import { RootState } from "../../stores/store";
import ReportsService from "../../services/reportsService";
import SnagTrackerService from "../../services/snagTrackerService";

const COLORS = ['#ef4444', '#f59e0b', '#10b981'];
const RESOLUTION_COLORS = ['#8b5cf6', '#06b6d4'];
const PRIORITY_COLORS = ['#dc2626', '#d97706', '#059669'];

const HomePage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [overview, setOverview] = useState<any>(null);
  const [recentActivities, setRecentActivities] = useState([
    { id: 1, action: "High priority snagging issue reported", time: "5 minutes ago", user: "Site Inspector" },
    { id: 2, action: "Project milestone completed", time: "30 minutes ago", user: "Project Manager" },
    { id: 3, action: "Snagging issue resolved - Plumbing", time: "2 hours ago", user: "Maintenance Team" },
    { id: 4, action: "Timeline updated for Block A", time: "4 hours ago", user: "Sarah Johnson" },
    { id: 5, action: "Quality inspection completed", time: "1 day ago", user: "QA Team" }
  ]);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const [overviewData, snagResponse] = await Promise.all([
          ReportsService.getDashboardOverview(),
          SnagTrackerService.getSnags(),
        ]);

        setOverview(overviewData);

        const activities = (snagResponse.snags || []).slice(0, 5).map((snag: any, index: number) => ({
          id: index + 1,
          action: snag.title,
          time: new Date(snag.createdDate).toLocaleDateString(),
          user: snag.assignedTo?.name || 'Unassigned',
        }));

        if (activities.length > 0) {
          setRecentActivities(activities);
        }
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      }
    };

    loadDashboard();
  }, []);

  // Data for Snagging Issue Status - Pie chart
  const snaggingStatusData = overview?.byStatus
    ? Object.entries(overview.byStatus).map(([name, value]) => ({ name, value: Number(value) }))
    : [
        { name: 'Open', value: 45 },
        { name: 'In Progress', value: 38 },
        { name: 'Closed', value: 44 }
      ];

  // Data for Snagging Issues Over Time - Line chart
  const issuesOverTimeData = overview?.timelineData?.length
    ? overview.timelineData.map((item: any) => ({
        name: item.date,
        Detected: item.count,
        Resolved: 0,
      }))
    : [
        { name: 'Week 1', Detected: 20, Resolved: 15 },
        { name: 'Week 2', Detected: 35, Resolved: 25 },
        { name: 'Week 3', Detected: 40, Resolved: 30 },
        { name: 'Week 4', Detected: 30, Resolved: 35 },
        { name: 'Week 5', Detected: 45, Resolved: 40 },
        { name: 'Week 6', Detected: 25, Resolved: 30 }
      ];

  // Data for Project Timeline Progress - Horizontal Bar chart
  const projectPhases = [
    { name: "Foundation", progress: 100, status: "completed" },
    { name: "Structure", progress: 85, status: "active" },
    { name: "MEP Installation", progress: 60, status: "active" },
    { name: "Interior Finishing", progress: 30, status: "active" },
    { name: "External Works", progress: 10, status: "pending" },
    { name: "Final Inspection", progress: 0, status: "pending" }
  ];

  // Data for Average Resolution Time - Horizontal Bar chart
  const avgResolutionData = [
    { name: "Plumbing", days: 3.2 },
    { name: "Electrical", days: 2.5 },
    { name: "Painting", days: 1.8 },
    { name: "HVAC", days: 4.1 }
  ];

  // Data for Issue Priority Breakdown - Bar chart
  const priorityData = overview?.byPriority
    ? Object.entries(overview.byPriority).map(([name, count]) => ({ name, count: Number(count) }))
    : [
        { name: "High", count: 32 },
        { name: "Medium", count: 48 },
        { name: "Low", count: 47 }
      ];

  // Upcoming Milestones
  const upcomingMilestones = [
    { title: "Block A Handover", date: "Sep 15", priority: "high" },
    { title: "MEP Testing Complete", date: "Sep 22", priority: "medium" },
    { title: "Landscaping Start", date: "Oct 05", priority: "low" },
    { title: "Final Quality Audit", date: "Oct 20", priority: "high" }
  ];

  // Custom label for pie chart showing total
  const renderCustomizedLabel = ({ cx, cy }: any) => {
    return (
      <g>
        <text x={cx} y={cy - 5} fill="#111827" textAnchor="middle" dominantBaseline="central" fontSize={20} fontWeight="bold">
          {overview?.totalSnags ?? 127}
        </text>
        <text x={cx} y={cy + 15} fill="#6b7280" textAnchor="middle" dominantBaseline="central" fontSize={12}>
          Total Issues
        </text>
      </g>
    );
  };

  return (
    <div className="dashboard-container">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">Welcome back, {user}</h1>
        </div>
        <div className="search-container">
          <div className="search-wrapper">
            <FiSearch className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Search snagging issues, projects, timelines..."
              className="search-input"
            />
          </div>
        </div>
      </div>

      {/* Snagging & Timeline Charts Grid */}
      <div className="kpi-charts-grid">

        {/* Snagging Issue Status Pie Chart */}
        <div className="chart-section">
          <div className="card">
            <div className="card-header">
              <div className="chart-title-container">
                <FiAlertCircle className="chart-icon" size={20} />
                <h2 className="card-title">Snagging Issue Status</h2>
              </div>
              <select className="time-selector">
                <option>This Week</option>
                <option>This Month</option>
                <option>Last 3 Months</option>
              </select>
            </div>
            <div className="chart-placeholder">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={snaggingStatusData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="45%"
                    outerRadius={65}
                    innerRadius={40}
                    label={renderCustomizedLabel}
                  >
                    {snaggingStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend 
                    verticalAlign="bottom" 
                    height={50}
                    formatter={(value: string | number, entry: any) => {
                      const payloadValue = entry && entry.payload && entry.payload.value ? entry.payload.value : 0;
                      return `${value}: ${payloadValue}`;
                    }}
                  />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Snagging Issues Over Time Line Chart */}
        <div className="chart-section">
          <div className="card">
            <div className="card-header">
              <div className="chart-title-container">
                <FiTrendingUp className="chart-icon" size={20} />
                <h2 className="card-title">Snagging Issues Over Time</h2>
              </div>
              <select className="time-selector">
                <option>Last 30 Days</option>
                <option>Last 90 Days</option>
                <option>Last 6 Months</option>
              </select>
            </div>
            <div className="chart-placeholder">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={issuesOverTimeData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false}
                    tickLine={false}
                    fontSize={12}
                    fill="#6b7280"
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    fontSize={12}
                    fill="#6b7280"
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="Detected" 
                    stroke={RESOLUTION_COLORS[0]} 
                    strokeWidth={3}
                    dot={{ fill: RESOLUTION_COLORS[0], strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 7 }} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="Resolved" 
                    stroke={RESOLUTION_COLORS[1]} 
                    strokeWidth={3}
                    dot={{ fill: RESOLUTION_COLORS[1], strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 7 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Project Timeline Progress Horizontal Bar Chart */}
        <div className="chart-section wide">
          <div className="card">
            <div className="card-header">
              <div className="chart-title-container">
                <FiActivity className="chart-icon" size={20} />
                <h2 className="card-title">Project Timeline Progress</h2>
              </div>
              <select className="time-selector">
                <option>All Phases</option>
                <option>Active Only</option>
                <option>Upcoming</option>
              </select>
            </div>
            <div className="chart-placeholder timeline-chart">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={projectPhases}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis 
                    type="number" 
                    domain={[0, 100]} 
                    axisLine={false}
                    tickLine={false}
                    fontSize={12}
                    fill="#6b7280"
                  />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    width={120}
                    axisLine={false}
                    tickLine={false}
                    fontSize={12}
                    fill="#6b7280"
                  />
                  <Tooltip 
                    formatter={(value) => [`${value}%`, 'Progress']}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Bar 
                    dataKey="progress" 
                    fill="#8b5cf6" 
                    radius={[0, 8, 8, 0]} 
                    animationDuration={1500}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Average Resolution Time Horizontal Bar Chart */}
        <div className="chart-section">
          <div className="card">
            <div className="card-header">
              <div className="chart-title-container">
                <FiClock className="chart-icon" size={20} />
                <h2 className="card-title">Avg. Resolution Time</h2>
              </div>
              <select className="time-selector">
                <option>By Category</option>
                <option>By Priority</option>
                <option>By Team</option>
              </select>
            </div>
            <div className="chart-placeholder">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={avgResolutionData}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis 
                    type="number" 
                    axisLine={false}
                    tickLine={false}
                    fontSize={12}
                    fill="#6b7280"
                  />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    width={80}
                    axisLine={false}
                    tickLine={false}
                    fontSize={12}
                    fill="#6b7280"
                  />
                  <Tooltip 
                    formatter={(value) => [`${value} days`, 'Resolution Time']}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Bar 
                    dataKey="days" 
                    fill="#8b5cf6" 
                    radius={[0, 8, 8, 0]} 
                    animationDuration={1500}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Issue Priority Breakdown Vertical Bar Chart */}
        <div className="chart-section">
          <div className="card">
            <div className="card-header">
              <div className="chart-title-container">
                <FiBarChart2 className="chart-icon" size={20} />
                <h2 className="card-title">Issue Priority Breakdown</h2>
              </div>
              <select className="time-selector">
                <option>This Month</option>
                <option>Last 3 Months</option>
                <option>All Time</option>
              </select>
            </div>
            <div className="chart-placeholder">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={priorityData}
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis 
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    fontSize={12}
                    fill="#6b7280"
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    fontSize={12}
                    fill="#6b7280"
                  />
                  <Tooltip 
                    formatter={(value) => [`${value}`, 'Issues']}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Bar 
                    dataKey="count" 
                    radius={[8, 8, 0, 0]}
                    animationDuration={1500}
                  >
                    {priorityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PRIORITY_COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Upcoming Milestones List */}
        <div className="chart-section">
          <div className="card">
            <div className="card-header">
              <div className="chart-title-container">
                <FiCalendar className="chart-icon" size={20} />
                <h2 className="card-title">Upcoming Milestones</h2>
              </div>
              <button className="view-all-btn">View Calendar</button>
            </div>
            <div className="chart-placeholder milestones-chart">
              <div className="milestones-container">
                {upcomingMilestones.map((milestone, index) => (
                  <div key={index} className={`milestone-item ${milestone.priority}`}>
                    <div className="milestone-date">
                      <span className="date-text">{milestone.date}</span>
                    </div>
                    <div className="milestone-info">
                      <span className="milestone-title">{milestone.title}</span>
                      <span className={`milestone-priority ${milestone.priority}`}>
                        {milestone.priority.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Recent Activity */}
      <div className="content-grid-single">
        <div className="activity-section">
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Recent Activity</h2>
              <button className="view-all-btn">View All</button>
            </div>
            <div className="activity-list">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="activity-item">
                  <div className="activity-avatar">
                    {activity.user.charAt(0)}
                  </div>
                  <div className="activity-content">
                    <div className="activity-action">{activity.action}</div>
                    <div className="activity-meta">
                      <span className="activity-user">{activity.user}</span>
                      <span className="activity-time">{activity.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;